<template>
  <div class="p-4">
    <!-- Page Header -->
    <div class="mb-4">
      <h1 class="text-xl font-bold text-gray-900 dark:text-white mb-0.5">
        Catálogos Dinámicos
      </h1>
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Gestión de catálogos y registros del sistema
      </p>
    </div>

    <!-- Selector de Catálogo -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-4">
      <div class="p-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Seleccionar Catálogo
            </label>
            <select
              v-model="catalogoSeleccionado"
              @change="onCatalogoChange"
              class="w-full px-2.5 py-1.5 text-sm rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
            >
              <option :value="null">Seleccione un catálogo...</option>
              <option v-for="catalogo in catalogos" :key="catalogo.catalogoId" :value="catalogo">
                {{ catalogo.nombreCatalogo }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Información sobre campos calculados -->
    <div v-if="catalogoSeleccionado && hasCamposCalculados" class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
      <div class="flex items-start gap-2">
        <svg class="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <div>
          <h3 class="text-xs font-semibold text-blue-900 dark:text-blue-200 mb-1">ℹ️ Este catálogo tiene campos con cálculos automáticos</h3>
          <p class="text-[11px] text-blue-700 dark:text-blue-300 mb-1.5">
            Debes ingresar un <strong>valor base</strong> para cada campo. Al usar este registro en un prototipo, el valor se multiplicará según las siguientes fórmulas:
          </p>
          <ul class="text-[11px] text-blue-600 dark:text-blue-400 space-y-0.5">
            <li v-for="campo in camposCalculados" :key="campo.campoId" class="flex items-start gap-1.5">
              <span class="font-semibold">{{ campo.nombreCampo }}:</span>
              <span>{{ getTipoCalculoFormula(campo.tipoCalculo) }}</span>
            </li>
          </ul>
          <p class="text-[11px] text-blue-700 dark:text-blue-300 mt-1.5 font-medium">
            💡 Ejemplo: Si ingresas $100 en un campo configurado como "Por área de papel", y el prototipo tiene 2 m² de área, el costo total será $100 × 2 = $200
          </p>
        </div>
      </div>
    </div>

    <!-- Tabla de Registros -->
    <div v-if="catalogoSeleccionado" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-3 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <h2 class="text-base font-semibold text-gray-900 dark:text-white">
            Registros de: {{ catalogoSeleccionado.nombreCatalogo }}
          </h2>
          <button
            @click="openModal()"
            class="bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 text-sm rounded-lg transition-colors duration-200 flex items-center gap-1.5"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Nuevo Registro
          </button>
        </div>
      </div>

      <div class="p-3">
        <div v-if="loading" class="text-center py-6">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mx-auto"></div>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-3 py-1.5 font-bold">Descripción</th>
                <th v-for="campo in campos" :key="campo.campoId" class="px-3 py-1.5 font-bold">
                  <div class="flex flex-col gap-0.5">
                    <span>{{ campo.nombreCampo }}</span>
                    <span v-if="campo.esCalculado" class="text-[9px] font-normal normal-case text-blue-600 dark:text-blue-400">
                      🔢 {{ getTipoCalculoLabel(campo.tipoCalculo) }}
                    </span>
                  </div>
                </th>
                <th class="px-3 py-1.5 font-bold">Estado</th>
                <th class="px-3 py-1.5 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="registro in registros"
                :key="registro.registroId"
                class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <td class="px-3 py-1.5 font-medium text-gray-900 dark:text-white">
                  {{ registro.descripcion }}
                </td>
                <td v-for="campo in campos" :key="campo.campoId" class="px-3 py-1.5 text-gray-700 dark:text-gray-200">
                  {{ getValorCampo(registro, campo.campoId) }}
                </td>
                <td class="px-3 py-1.5">
                  <span
                    :class="registro.activo ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'"
                    class="px-2 py-0.5 text-[10px] font-medium rounded-full"
                  >
                    {{ registro.activo ? 'Activo' : 'Inactivo' }}
                  </span>
                </td>
                <td class="px-3 py-1.5">
                  <div class="flex gap-1">
                    <button @click="openModal(registro)" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button @click="eliminarRegistro(registro)" class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="registros.length === 0">
                <td :colspan="campos.length + 3" class="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  No hay registros en este catálogo
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal para Crear/Editar Registro -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">
            {{ registroEditando ? 'Editar Registro' : 'Nuevo Registro' }}
          </h3>
        </div>

        <div class="p-4 space-y-3">
          <!-- Descripción -->
          <div>
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descripción *
            </label>
            <input
              v-model="formData.descripcion"
              type="text"
              class="w-full px-2.5 py-1.5 text-sm rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
              placeholder="Ingrese la descripción"
            />
          </div>

          <!-- Código (opcional) -->
          <div>
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Código
            </label>
            <input
              v-model="formData.codigoRegistro"
              type="text"
              class="w-full px-2.5 py-1.5 text-sm rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
              placeholder="Código opcional"
            />
          </div>

          <!-- Campos dinámicos -->
          <div v-for="campo in campos" :key="campo.campoId">
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              {{ campo.nombreCampo }}
              <span v-if="campo.esMonto" class="text-[10px] text-green-600 dark:text-green-400">(Monto)</span>
              <span v-if="campo.esCalculado" class="text-[10px] text-blue-600 dark:text-blue-400">
                (Calculado: {{ getTipoCalculoLabel(campo.tipoCalculo) }})
              </span>
              <span v-if="campo.esCatalogo" class="text-[10px] text-violet-600 dark:text-violet-400">
                (Catálogo)
              </span>
            </label>

            <!-- Descripción de la fórmula -->
            <div v-if="campo.esCalculado && campo.tipoCalculo" class="mb-1.5 p-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-[10px]">
              <span class="font-semibold text-blue-700 dark:text-blue-300">Fórmula: </span>
              <span class="text-blue-600 dark:text-blue-400">{{ getTipoCalculoFormula(campo.tipoCalculo) }}</span>
            </div>

            <!-- Campo tipo catálogo → select -->
            <select
              v-if="campo.esCatalogo && campo.catalogoReferencia"
              v-model="formData.valores[campo.campoId].valorTexto"
              class="w-full px-2.5 py-1.5 text-sm rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
            >
              <option :value="null">— Seleccionar —</option>
              <option
                v-for="item in (catalogosExternos[campo.catalogoReferencia] ?? [])"
                :key="item.id"
                :value="String(item.id)"
              >
                {{ item[CATALOGOS_DISPONIBLES[campo.catalogoReferencia]?.labelKey ?? 'contacto'] }}
              </option>
            </select>

            <!-- Campo numérico normal -->
            <input
              v-else
              v-model.number="formData.valores[campo.campoId].valorNumerico"
              type="number"
              step="0.0001"
              class="w-full px-2.5 py-1.5 text-sm rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
              :placeholder="campo.esCalculado ? 'Valor base (se multiplicará según la fórmula)' : 'Ingrese valor decimal'"
            />
          </div>

          <!-- Estado activo -->
          <div class="flex items-center">
            <input
              v-model="formData.activo"
              type="checkbox"
              class="w-3.5 h-3.5 text-violet-600 bg-gray-100 border-gray-300 rounded focus:ring-violet-500"
            />
            <label class="ml-2 text-xs font-medium text-gray-700 dark:text-gray-300">Activo</label>
          </div>
        </div>

        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            @click="closeModal"
            class="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
          >
            Cancelar
          </button>
          <button
            @click="guardarRegistro"
            :disabled="loading"
            class="px-3 py-1.5 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition disabled:opacity-50"
          >
            {{ loading ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useCatalogosNew } from '../../composables/useCatalogosNew'
import api from '@/utils/axiosConfig'

const {
  loading,
  catalogos,
  campos,
  registros,
  fetchCatalogos,
  fetchCamposByCatalogo,
  fetchRegistrosByCatalogo,
  createRegistro,
  updateRegistro,
  deleteRegistro
} = useCatalogosNew()

const catalogoSeleccionado = ref(null)
const showModal = ref(false)
const registroEditando = ref(null)

// Mapa de registros por catalogoReferencia para campos tipo catálogo
const catalogosExternos = ref({})

const CATALOGOS_DISPONIBLES = {
  proveedores: { endpoint: '/proveedores', labelKey: 'contacto' },
}

const cargarCatalogoExterno = async (referencia) => {
  if (catalogosExternos.value[referencia]) return
  const config = CATALOGOS_DISPONIBLES[referencia]
  if (!config) return
  try {
    const res = await api.get(config.endpoint)
    catalogosExternos.value[referencia] = res.data
  } catch {
    catalogosExternos.value[referencia] = []
  }
}

const getLabelCatalogoExterno = (referencia, id) => {
  const config = CATALOGOS_DISPONIBLES[referencia]
  if (!config) return id
  const lista = catalogosExternos.value[referencia] ?? []
  const item = lista.find(i => String(i.id) === String(id))
  return item ? item[config.labelKey] : id
}

const formData = ref({
  descripcion: '',
  codigoRegistro: '',
  activo: true,
  valores: {}
})

// Computed para detectar si hay campos calculados
const hasCamposCalculados = computed(() => {
  return campos.value.some(campo => campo.esCalculado)
})

const camposCalculados = computed(() => {
  return campos.value.filter(campo => campo.esCalculado)
})

onMounted(async () => {
  await fetchCatalogos()
})

const onCatalogoChange = async () => {
  if (catalogoSeleccionado.value) {
    await fetchCamposByCatalogo(catalogoSeleccionado.value.catalogoId)
    await fetchRegistrosByCatalogo(catalogoSeleccionado.value.catalogoId)
    // Pre-cargar catálogos externos que necesiten los campos
    campos.value.filter(c => c.esCatalogo && c.catalogoReferencia).forEach(c => {
      cargarCatalogoExterno(c.catalogoReferencia)
    })
  }
}

const initFormData = () => {
  formData.value = {
    descripcion: '',
    codigoRegistro: '',
    activo: true,
    valores: {}
  }

  campos.value.forEach(campo => {
    formData.value.valores[campo.campoId] = {
      campoId: campo.campoId,
      valorNumerico: null,
      valorTexto: null,
    }
    if (campo.esCatalogo && campo.catalogoReferencia) {
      cargarCatalogoExterno(campo.catalogoReferencia)
    }
  })
}

const openModal = (registro = null) => {
  initFormData()

  if (registro) {
    registroEditando.value = registro
    formData.value.descripcion = registro.descripcion
    formData.value.codigoRegistro = registro.codigoRegistro || ''
    formData.value.activo = registro.activo

    if (registro.valores_campo) {
      registro.valores_campo.forEach(valor => {
        if (formData.value.valores[valor.campoId]) {
          formData.value.valores[valor.campoId] = {
            campoId: valor.campoId,
            valorNumerico: valor.valorNumerico ?? null,
            valorTexto: valor.valorTexto ?? null,
          }
        }
      })
    }
  } else {
    registroEditando.value = null
  }

  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  registroEditando.value = null
  initFormData()
}

const guardarRegistro = async () => {
  try {
    const data = {
      catalogoId: catalogoSeleccionado.value.catalogoId,
      descripcion: formData.value.descripcion,
      codigoRegistro: formData.value.codigoRegistro,
      activo: formData.value.activo,
      valores: Object.values(formData.value.valores)
    }

    if (registroEditando.value) {
      await updateRegistro(registroEditando.value.registroId, data)
    } else {
      await createRegistro(data)
    }

    closeModal()
    await fetchRegistrosByCatalogo(catalogoSeleccionado.value.catalogoId)
  } catch (error) {
    console.error('Error al guardar registro:', error)
  }
}

const eliminarRegistro = async (registro) => {
  if (confirm(`¿Está seguro de eliminar el registro "${registro.descripcion}"?`)) {
    try {
      await deleteRegistro(registro.registroId, catalogoSeleccionado.value.catalogoId)
    } catch (error) {
      console.error('Error al eliminar registro:', error)
    }
  }
}

const getValorCampo = (registro, campoId) => {
  if (!registro.valores_campo) return '-'
  const valor = registro.valores_campo.find(v => v.campoId === campoId)
  if (!valor) return '-'

  if (valor.valorTexto !== null && valor.valorTexto !== undefined) {
    const campo = campos.value.find(c => c.campoId === campoId)
    if (campo?.esCatalogo && campo.catalogoReferencia) {
      return getLabelCatalogoExterno(campo.catalogoReferencia, valor.valorTexto)
    }
    return valor.valorTexto
  }
  if (valor.valorNumerico !== null && valor.valorNumerico !== undefined) return valor.valorNumerico
  return '-'
}

// Helper para mostrar el tipo de cálculo de forma amigable
const getTipoCalculoLabel = (tipoCalculo) => {
  const labels = {
    'valor_directo': 'Directo',
    'area_papel': 'Por área de papel',
    'area_corrugado': 'Por área de corrugado',
    'division_cantidad_paquete': 'División por cant/paquete',
    'division_cantidad_tarima': 'División por tarima',
    'division_envio': 'División por envío'
  }
  return labels[tipoCalculo] || tipoCalculo
}

// Helper para mostrar la fórmula matemática
const getTipoCalculoFormula = (tipoCalculo) => {
  const formulas = {
    'valor_directo': 'Costo base (sin cálculo)',
    'area_papel': 'Costo × Área Papel (m²)',
    'area_corrugado': 'Costo × Área Corrugado (m²)',
    'division_cantidad_paquete': 'Costo ÷ Cantidad por Paquete',
    'division_cantidad_tarima': 'Costo ÷ (Cantidad por Paquete × Paquetes por Tarima)',
    'division_envio': 'Costo ÷ (Cantidad por Paquete × Paquetes por Tarima × Tar x Unidad)'
  }
  return formulas[tipoCalculo] || 'Fórmula no definida'
}
</script>
